const RETRYABLE_METHODS = new Set(['GET', 'HEAD']);

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Wraps a fetch (SvelteKit's on the server) so a read that hits a network
 * error or a 5xx is tried again, with 500 ms then 1 s between attempts. The
 * website API has had short bursts of 500s that a second attempt gets past;
 * without this, each one is a failed page render (or ISR revalidation) and a
 * Sentry event. Only GET and HEAD are retried: repeating a write isn't safe.
 */
export const withServerErrorRetry =
	(fetchFn: typeof globalThis.fetch, maxAttempts = 3, delayMs = 500) =>
	async (
		input: Parameters<typeof globalThis.fetch>[0],
		init?: Parameters<typeof globalThis.fetch>[1]
	): Promise<Response> => {
		const method = (
			init?.method ?? (input instanceof Request ? input.method : 'GET')
		).toUpperCase();

		if (!RETRYABLE_METHODS.has(method)) {
			return fetchFn(input, init);
		}

		for (let attempt = 1; ; attempt++) {
			const isLastAttempt = attempt === maxAttempts;
			// A Request can only be sent once, so each attempt sends a copy.
			const request = input instanceof Request ? input.clone() : input;

			try {
				const response = await fetchFn(request, init);

				if (response.status < 500 || isLastAttempt) {
					return response;
				}
			} catch (error) {
				if (isLastAttempt) {
					throw error;
				}
			}

			await wait(delayMs * 2 ** (attempt - 1));
		}
	};
